import React, { useState, useMemo } from 'react';
import { useSearchState } from '../../hooks/useSearchState';
import {
  extractCount,
  extractDisplayName,
  mapSubGroupToTopGroup,
} from '../../utils/helpers';
import { VARIANT_GROUP_ORDER } from '../../utils/constants';
import type { SearchState, Filter } from '@elastic/search-ui';
import posthog from 'posthog-js';
import { GroupData, SubGroupData } from '../../types';

export const HierarchicalVariantFacet: React.FC = () => {
  const { currentState, driver } = useSearchState();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Get currently selected variant filters to preserve them even with 0 results
  const getSelectedVariantFilters = (): string[] => {
    if (!currentState?.filters) return [];

    const selectedVariants: string[] = [];
    currentState.filters.forEach((filter: Filter) => {
      if (filter.field === 'listing_variant_sub_group') {
        filter.values.forEach((value: any) => {
          if (value != null && value !== undefined) {
            selectedVariants.push(value.toString());
          }
        });
      }
    });
    return selectedVariants;
  };

  // Group variants by top_group and sub_group using FACET DATA (not results)
  const groupedVariants = useMemo((): Record<string, GroupData> => {
    const groups: Record<string, GroupData> = {};
    const selectedVariants = getSelectedVariantFilters();

    // Use facet data which contains aggregated counts for ALL results
    const subGroupFacet = currentState?.facets?.listing_variant_sub_group;
    const displayNameFacet = currentState?.facets?.listing_pab_variant_txt;

    // Process all sub groups from facet data
    let subGroupData: any[] = [];
    let displayNameData: any[] = [];

    // Extract the actual data arrays from the facet structure
    if (Array.isArray(subGroupFacet)) {
      subGroupFacet.forEach((facetItem: any) => {
        if (facetItem && facetItem.data && Array.isArray(facetItem.data)) {
          subGroupData = [...subGroupData, ...facetItem.data];
        } else if (facetItem && facetItem.value != null) {
          subGroupData.push(facetItem);
        }
      });
    }

    if (Array.isArray(displayNameFacet)) {
      displayNameFacet.forEach((facetItem: any) => {
        if (facetItem && facetItem.data && Array.isArray(facetItem.data)) {
          displayNameData = [...displayNameData, ...facetItem.data];
        } else if (facetItem && facetItem.value != null) {
          displayNameData.push(facetItem);
        }
      });
    }

    // Process the extracted data
    if (Array.isArray(subGroupData)) {
      subGroupData.forEach((item) => {
        if (!item || typeof item.value !== 'string' || !item.value.trim()) {
          return;
        }

        const subGroup = item.value;
        const count = item.count || 0;

        // Skip if count is 0 or invalid (but we'll still process selected ones later)
        if (count <= 0 && !selectedVariants.includes(subGroup)) {
          return;
        }

        // Map sub group to top group
        const topGroup = mapSubGroupToTopGroup(subGroup);

        // ── CHANGE: skip anything that maps to 'Other' — these are stray
        // build values (Singer, RUF, custom, etc.) that should never appear
        // as a variant group in the sidebar.
        if (topGroup === 'Other') {
          return;
        }

        if (!groups[topGroup]) {
          groups[topGroup] = { count: 0, subGroups: {} };
        }

        // Use the exact value from listing_pab_variant_txt as display name
        let displayName = subGroup;
        if (Array.isArray(displayNameData)) {
          const displayItem = displayNameData.find(
            (d: any) => d.value === subGroup
          );
          if (displayItem && displayItem.value) {
            displayName = displayItem.value;
          }
        }

        groups[topGroup].subGroups[subGroup] = {
          count: count,
          displayName: displayName,
        };

        // Update top group count
        groups[topGroup].count += count;
      });
    }

    // Add selected variants that might not be in current facet data (0 results case)
    selectedVariants.forEach((subGroup) => {
      if (!subGroup || typeof subGroup !== 'string') return;

      const topGroup = mapSubGroupToTopGroup(subGroup);

      // ── CHANGE: same guard — don't resurrect 'Other' groups from selections
      if (topGroup === 'Other') return;

      // Check if this subGroup already exists in our groups
      let exists = false;
      if (groups[topGroup] && groups[topGroup].subGroups[subGroup]) {
        exists = true;
      }

      // If it doesn't exist in current data, add it with count 0
      if (!exists) {
        if (!groups[topGroup]) {
          groups[topGroup] = { count: 0, subGroups: {} };
        }

        let displayName = subGroup;
        if (Array.isArray(displayNameData)) {
          const displayItem = displayNameData.find(
            (d: any) => d.value === subGroup
          );
          if (displayItem && displayItem.value) {
            displayName = displayItem.value;
          }
        }

        groups[topGroup].subGroups[subGroup] = {
          count: 0,
          displayName: displayName,
        };
      }
    });

    // Sort groups by fixed order
    const sortedGroups: Record<string, GroupData> = {};
    VARIANT_GROUP_ORDER.forEach((groupName) => {
      if (groups[groupName]) {
        sortedGroups[groupName] = groups[groupName];
      }
    });

    // ── CHANGE: only append extra groups that are explicitly in VARIANT_GROUP_ORDER.
    // This means 'Other' and any future unknown group will never appear,
    // even if it somehow makes it through the guards above.
    Object.entries(groups).forEach(([groupName, groupData]) => {
      if (
        !VARIANT_GROUP_ORDER.includes(groupName) &&
        groupName !== 'Other'
      ) {
        sortedGroups[groupName] = groupData;
      }
    });

    return sortedGroups;
  }, [currentState?.facets, currentState?.filters]);

  // Check if a sub-group is currently filtered
  const isSubGroupSelected = (subGroup: string) => {
    if (!currentState?.filters) return false;

    return currentState.filters.some(
      (filter: Filter) =>
        filter.field === 'listing_variant_sub_group' &&
        filter.values.includes(subGroup)
    );
  };

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const handleSubGroupToggle = (subGroup: string, displayName: string) => {
    const isCurrentlySelected = isSubGroupSelected(subGroup);

    if (isCurrentlySelected) {
      driver.removeFilter('listing_variant_sub_group', subGroup);
    } else {
      driver.addFilter('listing_variant_sub_group', subGroup, 'any');
    }

    posthog.capture('variant_filter_applied', {
      sub_group: subGroup,
      display_name: displayName,
      action: isCurrentlySelected ? 'removed' : 'added',
    });
  };

  const clearAllVariantFilters = () => {
    currentState?.filters?.forEach((filter: Filter) => {
      if (filter.field === 'listing_variant_sub_group') {
        filter.values.forEach((value: any) => {
          driver.removeFilter(filter.field, value);
        });
      }
    });
  };

  const selectAllInGroup = (group: string, data: GroupData) => {
    Object.keys(data.subGroups).forEach((sg) => {
      if (!isSubGroupSelected(sg)) {
        handleSubGroupToggle(sg, (data.subGroups[sg] as SubGroupData).displayName);
      }
    });
  };

  const clearAllInGroup = (group: string, data: GroupData) => {
    Object.keys(data.subGroups).forEach((sg) => {
      if (isSubGroupSelected(sg)) {
        handleSubGroupToggle(sg, (data.subGroups[sg] as SubGroupData).displayName);
      }
    });
  };

  const hasVariantFilters = currentState?.filters?.some(
    (filter: Filter) => filter.field === 'listing_variant_sub_group'
  );

  // Don't render if no groups and no filters
  if (Object.keys(groupedVariants).length === 0 && !hasVariantFilters) {
    return null;
  }

  return (
    <div className="custom-facet-container group">
      <div className="custom-facet-header flex justify-between items-center border-t border-mp-border pt-2" style={{ marginBottom: 0 }}>
        <span className="font-display text-xs uppercase tracking-widest text-mp-muted">Variant</span>
        {hasVariantFilters && (
          <button
            onClick={clearAllVariantFilters}
            className="text-primary font-mono text-[10px] uppercase transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex flex-col pb-2">
        {Object.entries(groupedVariants).map(([group, data]) => {
          const isExpanded = expandedGroups.has(group);
          const hasSelectedSub = Object.keys(data.subGroups).some(sg => isSubGroupSelected(sg));
          const subGroupCount = Object.keys(data.subGroups).length;

          return (
            <div key={group}>
              <div
                className="flex items-center w-full pt-1 cursor-pointer group"
                onClick={() => toggleGroup(group)}
              >
                <span className={`font-display text-xs uppercase tracking-widest flex-1 ${hasSelectedSub ? 'text-mp-text' : 'text-mp-muted'}`}>
                  &#x203A; {group}
                  <span className={`ml-1.5 text-[10px] font-mono ${hasSelectedSub ? 'text-primary' : 'text-mp-muted'}`}>
                    ({data.count})
                  </span>
                </span>
                <div className="flex items-center gap-3">
                  {isExpanded && subGroupCount > 3 && (
                    <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                      <button
                        onClick={() => selectAllInGroup(group, data)}
                        className="text-mp-muted font-mono text-[10px] uppercase hover:text-mp-text transition-colors"
                      >
                        All
                      </button>
                      <button
                        onClick={() => clearAllInGroup(group, data)}
                        className="text-mp-muted font-mono text-[10px] uppercase hover:text-mp-text transition-colors"
                      >
                        None
                      </button>
                    </div>
                  )}
                  <span className="material-symbols-outlined text-mp-muted text-[18px] opacity-0 group-hover:opacity-100 transition-opacity">
                    {isExpanded ? 'expand_less' : 'expand_more'}
                  </span>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-1 flex flex-col max-h-60 overflow-y-auto custom-scrollbar">
                  {Object.entries(data.subGroups)
                    .sort(([, a], [, b]) => {
                      const subDataA = a as SubGroupData;
                      const subDataB = b as SubGroupData;
                      if (group === 'Carreras') {
                        if (subDataA.displayName.toLowerCase().includes('other')) return 1;
                        if (subDataB.displayName.toLowerCase().includes('other')) return -1;
                      }
                      return subDataB.count - subDataA.count;
                    })
                    .map(([subGroup, subData]) => {
                      const subGroupData = subData as SubGroupData;
                      return (
                        <label
                          key={subGroup}
                          className={`custom-facet-option ${isSubGroupSelected(subGroup) ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSubGroupSelected(subGroup)}
                            onChange={() =>
                              handleSubGroupToggle(subGroup, subGroupData.displayName)
                            }
                            className="custom-facet-checkbox"
                          />
                          <span className="flex-1">{subGroupData.displayName}</span>
                          <span className="custom-facet-count">({subGroupData.count})</span>
                        </label>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
